import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { FileUp, Lock, LockOpen, MegaphoneOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import Reveal from "../../ui/Reveal";
import SectionTitle from "../shared/SectionTitle";
import { faceStyle, type Face } from "./faces";

type Key = "fonts" | "backgrounds" | "import" | "ads";
type Copy = { title: string; body: string };

const cell = "relative flex h-full flex-col overflow-hidden rounded-lg border border-line bg-surface p-[clamp(22px,2.6vw,34px)]";
const h3 = "mb-1.5 text-balance font-display text-[clamp(1.25rem,1.05rem+.7vw,1.6rem)] font-extrabold leading-[1.35]";
const body = "max-w-[38ch] leading-[1.9] text-ink-2";
const GLYPHS: Face[] = [
  ["peyda", 900],
  ["modam", 300],
  ["yekan", 200],
];

// "What changes with Pro": exactly four cells of different sizes, one per real Pro feature.
export default function Bento() {
  const { t } = useTranslation();
  const copy = (k: Key): Copy => ({ title: t(`ui.landing.features.items.${k}.title`), body: t(`ui.landing.features.items.${k}.body`) });

  return (
    <section id="features" aria-labelledby="features-title" className="mx-auto w-full max-w-[1200px] scroll-mt-[var(--nav-h)] px-[var(--gutter)] pb-[clamp(64px,9vw,128px)] pt-[clamp(40px,6vw,88px)]">
      <SectionTitle id="features-title" title={t("ui.landing.features.title")} className="mb-[clamp(28px,4vw,52px)]" />
      <div className="grid gap-4 min-[600px]:grid-cols-2 min-[900px]:grid-cols-12 min-[900px]:gap-5">
        <Reveal className="min-[600px]:col-span-2 min-[900px]:col-[5/span_8] min-[900px]:row-start-1">
          <FontsCell {...copy("fonts")} />
        </Reveal>
        <Reveal delay={0.08} className="min-[900px]:col-[1/span_4] min-[900px]:row-[1/span_2]">
          <BackgroundsCell {...copy("backgrounds")} />
        </Reveal>
        <Reveal delay={0.16} className="min-[900px]:col-[5/span_5] min-[900px]:row-start-2">
          <ImportCell {...copy("import")} />
        </Reveal>
        <Reveal delay={0.24} className="min-[600px]:col-span-2 min-[900px]:col-[10/span_3] min-[900px]:row-start-2">
          <AdsCell {...copy("ads")} />
        </Reveal>
      </div>
    </section>
  );
}

// Locked until the cell is actually seen, then it opens. "idle" (server render, reduced motion) already looks open.
function FontsCell({ title, body: text }: Copy) {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const seen = useInView(ref, { amount: 0.6, once: true });
  const [state, setState] = useState<"idle" | "locked" | "open">("idle");
  const locked = state === "locked";

  useEffect(() => {
    if (!matchMedia("(prefers-reduced-motion: reduce)").matches) setState("locked");
  }, []);
  useEffect(() => {
    if (!seen || !locked) return;
    const id = setTimeout(() => setState("open"), 380);
    return () => clearTimeout(id);
  }, [seen, locked]);

  const layer = "col-start-1 row-start-1 flex items-center justify-center gap-2 pe-3.5 ps-2.5 transition-opacity duration-[450ms] ease-out";
  return (
    <article ref={ref} className={cell}>
      <span
        aria-hidden
        className={cn(
          "absolute end-5 top-5 grid h-9 overflow-hidden rounded-md font-display text-[13.5px] font-bold",
          state === "open" && "motion-safe:animate-[b-pop_.5s_cubic-bezier(.16,1,.3,1)]",
        )}
      >
        <span className={cn(layer, "bg-accent text-on-accent", locked && "opacity-0")}>
          <LockOpen className={cn("size-[18px] transition-transform duration-[600ms] ease-out", locked && "-rotate-[24deg] scale-[.6]")} strokeWidth={1.75} />
          {t("ui.landing.b.lock.open")}
        </span>
        <span className={cn(layer, "bg-bg-2 text-ink-2", !locked && "opacity-0")}>
          <Lock className="size-[18px]" strokeWidth={1.75} />
          {t("ui.landing.b.lock.locked")}
        </span>
      </span>
      <div aria-hidden className="flex flex-wrap items-end gap-x-[clamp(14px,3vw,44px)] gap-y-2 pb-7 pt-11">
        {GLYPHS.map((f, i) => (
          <div
            key={f[0]}
            className={cn("transition-[opacity,transform] duration-[800ms] ease-out", locked && "translate-y-3 opacity-[.14]")}
            style={{ transitionDelay: locked ? "0ms" : `${i * 120 + 150}ms` }}
          >
            <span className="block text-[clamp(2.5rem,1.2rem+5.2vw,5.5rem)] leading-[1.3] text-ink" style={faceStyle(f)}>
              سلام
            </span>
            <small className="mt-0.5 block text-[13px] text-ink-3">{t(`ui.landing.b.fonts.${f[0]}`)}</small>
          </div>
        ))}
      </div>
      <div className="mt-auto">
        <h3 className={h3}>{title}</h3>
        <p className={body}>{text}</p>
      </div>
    </article>
  );
}

// Two real backgrounds as small prints that fan apart while the cell scrolls in, showing there is more than one.
function BackgroundsCell({ title, body: text }: Copy) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start 92%", "center 48%"] });
  const prints = [
    { src: "/img/slide_1.png", rotate: useTransform(p, [0, 1], [0, 4]), y: useTransform(p, [0, 1], [34, 0]), cls: "start-[2%] top-[4%] min-[900px]:top-[14%] motion-reduce:![transform:rotate(4deg)]" },
    { src: "/img/slide_3.png", rotate: useTransform(p, [0, 1], [0, -5]), y: useTransform(p, [0, 1], [-46, 0]), cls: "end-[2%] top-[40%] min-[900px]:top-[50%] motion-reduce:![transform:rotate(-5deg)]" },
  ];
  return (
    <article className={cn(cell, "bg-bg-2")}>
      <div ref={ref} aria-hidden className="relative mb-6 h-[230px] min-[900px]:min-h-[300px] min-[900px]:flex-1">
        {prints.map((pr) => (
          <motion.div
            key={pr.src}
            style={{ rotate: pr.rotate, y: pr.y }}
            className={cn("absolute aspect-[8/5] w-[min(210px,66%)] overflow-hidden rounded-md border-[5px] border-raised bg-raised shadow-card", pr.cls)}
          >
            {/* top of the photo only: the bottom 40% carries a baked-in caption */}
            <img src={pr.src} width={202} height={266} alt="" loading="lazy" decoding="async" className="size-full object-cover object-top" />
          </motion.div>
        ))}
      </div>
      <div className="mt-auto">
        <h3 className={h3}>{title}</h3>
        <p className={body}>{text}</p>
      </div>
    </article>
  );
}

function ImportCell({ title, body: text }: Copy) {
  const { t } = useTranslation();
  return (
    <article className={cell}>
      <div className="flex h-full flex-col gap-[22px] min-[900px]:flex-row min-[900px]:items-end">
        <div className="relative aspect-square w-[140px] shrink-0 overflow-hidden rounded-md">
          <img
            src="/img/slide_5.png"
            width={202}
            height={266}
            alt={t("ui.landing.b.importAlt")}
            loading="lazy"
            decoding="async"
            className="absolute left-1/2 top-0 h-auto w-[155%] max-w-none -translate-x-1/2"
          />
        </div>
        <div className="mt-auto min-[900px]:mt-0">
          <span aria-hidden className="mb-3 grid size-10 place-items-center rounded-md bg-accent-soft text-accent-text">
            <FileUp className="size-5" strokeWidth={1.75} />
          </span>
          <h3 className={h3}>{title}</h3>
          <p className={body}>{text}</p>
        </div>
      </div>
    </article>
  );
}

function AdsCell({ title, body: text }: Copy) {
  return (
    <article className={cn(cell, "min-h-[220px] border-transparent bg-accent text-on-accent")}>
      <MegaphoneOff className="mb-6 size-[52px]" strokeWidth={1.75} aria-hidden />
      <div className="mt-auto">
        <h3 className={h3}>{title}</h3>
        <p className={cn(body, "text-on-accent")}>{text}</p>
      </div>
    </article>
  );
}
